import { Component, ElementRef, EventEmitter, Injector, Input, OnInit, Output, ViewChild } from '@angular/core';
import { environment } from '../../../../environments/environment';
import { LookupGetService } from '../../service/lookup-service/lookup-get.service';
import { ListColumn, ListDataColumn } from '../../ui/Data/ListColumn';
import { DateUtil } from '../../ui/DateUtil';
import { Art57Section } from '../../ui/Art57Section/Art57Section';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged, switchMap, tap, startWith, filter } from 'rxjs/operators';
import { EMPTY } from 'rxjs';

@Component({
  selector: 'app-lookup-finder',
  templateUrl: './lookup-finder.component.html',
  styleUrl: './lookup-finder.component.scss'
})
export class LookupFinderComponent implements OnInit {
  @Input() lookup!: any;
  @Output() selectedElement: EventEmitter<any> = new EventEmitter<any>();
  @Input() selectedValue: any;
  @ViewChild('filterBox') filterBox!: ElementRef;
  @Input() isCondition!: boolean;

  items: any[] = []
  pageOfItems?: Array<any>;
  sortProperty: string = '';
  sortOrder = 1;
  loading = false;

  control = new FormControl('');
  private readonly FILTER_DEBOUNCE_MS = environment.lookupDebounceMs;

  constructor(private injector: Injector) {

  }

  /**
   * Handles search input and ENTER key behavior (select, load all, or search)
   * The lookup search is debounced to avoid excessive backend requests.
   */
  ngOnInit(): void {
    // Initialize FormControl from @Input
    var initialValue = '';
    if (typeof this.getSelectedValueTemplate() === 'number') {
      initialValue = this.getSelectedValueTemplate().toString();
    } else {
      initialValue = this.getSelectedValueTemplate()?.trim();
    }
    this.control.setValue(initialValue, { emitEvent: false });

    setTimeout(() => this.filterBox.nativeElement.focus(), 0);

    
    if (initialValue && initialValue.trim().length > 0) {
      this.loading = true;

      this.injector
        .get(LookupGetService)
        .filter(this.lookup.name, 'cn:' + initialValue.trim(), this.lookup.parameters)
        .subscribe({
          next: data => this.handleLookupResult(data),
          error: err => this.handleLookupError(err)
        });
    }


    this.control.valueChanges.pipe(
      debounceTime(this.FILTER_DEBOUNCE_MS),
      distinctUntilChanged(),
      filter(text => !!text), 
      tap(() => this.loading = true),
      switchMap(value => {
        const text = value?.trim();

        return this.injector
          .get(LookupGetService)
          .filter(this.lookup.name, 'cn:' + text, this.lookup.parameters);
          
      })
    ).subscribe({
      next: data => this.handleLookupResult(data),
      error: err => this.handleLookupError(err)
    });
  }

  /* Populate the result items, update pagination, and stop the loading indicator */
  private handleLookupResult(data: any[]): void {
    this.items = data.map((item, index) => ({
      ...item,
      num: (index + 1).toString().padStart(4, '0')
    }));

    this.pageOfItems = this.items.length === 0 ? [] : this.pageOfItems;
    this.loading = false;
  }

  /* Bubble up the error and hide the loading spinner */
  private handleLookupError(error?: any): void {
    this.loading = false;
    throw error;
  }

  /**
   * Handles ENTER key behavior:
   * - Selects the item when exactly one result is present
   * - Triggers an unfiltered lookup when no search criteria are provided (this should not occur because lookup has always some data with empty filter.)
   */
  onEnter(): void {
    const text = this.control.value?.trim();

    if (this.items.length === 1) {
      this.select(this.items[0]);
      return;
    }

    if (!text) {
      this.loading = true;

      this.injector
        .get(LookupGetService)
        .getAll(this.lookup.name, this.lookup.parameters)
        .subscribe({
          next: data => this.handleLookupResult(data),
          error: err => this.handleLookupError(err)
        });
        
      return;
    }
  }

  onPaste(event: ClipboardEvent) {
    event.preventDefault();

    const pastedText = event.clipboardData?.getData('text') || '';
    let cleanText = pastedText
      .split(/\r?\n/)                       // separa por líneas
      .map(s => s.replace(/"/g, ''))        // elimina comillas dobles
      .map(s => s.trim())                   // quita tabs y espacios delante/detrás
      .filter(Boolean)                      // elimina líneas vacías
      .join('|');                           // únelas con |

    const target = event.target as HTMLTextAreaElement;
    const start = target.selectionStart;
    const end = target.selectionEnd;

    if (cleanText.lastIndexOf('|') === cleanText.length - 1) {
      cleanText = cleanText.substring(0, cleanText.length - 1);
    }

    this.filterBox.nativeElement.value =
      this.filterBox.nativeElement.value.substring(0, start) +
      cleanText +
      this.filterBox.nativeElement.value.substring(end);

    setTimeout(() => {
      target.selectionStart = target.selectionEnd = start + cleanText.length;
    });
  }

  select(item: any) {

    //Default label
    var params = { selectedCode: item.code, selectedName: item.name };

    //Customized label
    switch (this.lookup.name) {
      case "qppv":
        params = { selectedCode: item.code, selectedName: item.name + " (" + item.organisationID + ")" };
        break;
      case "mfl":
        params = { selectedCode: item.code, selectedName: item.countrycode + " - " + item.city };
        break;
    }

    this.selectedElement.emit(params);
  }

  onChangePage(pageOfItems: Array<any>) {
    this.pageOfItems = pageOfItems;
  }

  sortBy(column: ListColumn) {
    this.sortOrder = column.getSourceName() === this.sortProperty ? (this.sortOrder * -1) : 1;
    this.sortProperty = column.getSourceName();
    this.items = [...this.items.sort((a: any, b: any) => {
      let result = 0;

      const valueA = a[this.sortProperty];
      const valueB = b[this.sortProperty];

      if (column.getDataType() === ListDataColumn.DATE || column.getDataType() === ListDataColumn.DATETIME) {
        const timeA = new Date(valueA).getTime(); // Convert to timestamp
        const timeB = new Date(valueB).getTime(); // Convert to timestamp
        result = timeA < timeB ? -1 : timeA > timeB ? 1 : 0;
      } else if (column.getDataType() === ListDataColumn.LOOKUP) {
        const valueA = this.injector.get(LookupGetService).retrieveValueFromCache(column.getLookup()!, a[column.getSourceName()]);
        const valueB = this.injector.get(LookupGetService).retrieveValueFromCache(column.getLookup()!, b[column.getSourceName()]);
        result = String(valueA).localeCompare(String(valueB)); 
      } else {
        // Case‑insensitive text compare
        const strA = (valueA ?? '').toString().toLowerCase();
        const strB = (valueB ?? '').toString().toLowerCase();
        result = String(strA).localeCompare(String(strB)); 
      }
      return result * this.sortOrder;
    })];
  }

  sortIcon(property: string) {
    if (property === this.sortProperty) {
      return this.sortOrder === 1 ? '↑' : '↓';
    }
    return '';
  }

  displayValue(value: any, dataType: ListDataColumn, lookup: string): string {
    let formatedValue: string = '';
    switch (dataType) {
      case ListDataColumn.DATE:
        formatedValue = DateUtil.getDateFormat(value);
        break;
      case ListDataColumn.DATETIME:
        formatedValue = String(value).includes('*') ? value : DateUtil.getDateTimeFormat(value);
        break;
      case ListDataColumn.YESNODATETIME:
        formatedValue = DateUtil.getYesNoDateTimeFormat(value);
        break;
      case ListDataColumn.LOOKUP:
        formatedValue = String(value).includes('*') ? value : this.injector.get(LookupGetService).retrieveValueFromCache(lookup, value);
        break;
      default:
        formatedValue = value;
    }
    return formatedValue;
  }

  getMaxNumberRowsReturned(): number {
    return environment.maxNumberRowsReturned
  }

  getLookupColumnsLength() {
    return this.lookup && this.lookup.columns ? this.lookup.columns.length + 1 : 1;
  }

  getLookupNameByType() {
    let lookUpNameByType: string = '';

    if (this.lookup) {
      lookUpNameByType = this.lookup.name;

      switch (this.lookup.name.toLowerCase()) {
        case "organisation":
          if (this.lookup.parameters && Array.isArray(this.lookup.parameters)) {
            if (this.lookup.parameters[0][0] === 'type' && this.lookup.parameters[0][1] === 1)
              lookUpNameByType = 'mah';
            else if (this.lookup.parameters[0][0] === 'type' && this.lookup.parameters[0][1] === 2)
              lookUpNameByType = 'sponsor';
          }
          break;
        case "substance":
          if (this.lookup.parameters && Array.isArray(this.lookup.parameters)) {
            if (this.lookup.parameters[0][0] === 'type' && this.lookup.parameters[0][1] === 2)
              lookUpNameByType = 'substanceapproved';
          }
          break;
      }
    }

    return lookUpNameByType;
  }

  getPlaceHolderTemplate(): string {
    // Customized placeholder
    switch (this.getLookupNameByType().toLowerCase()) {
      case "adminroute":
        return "Search for routes of administration available in the XEVMPD [visibility restrictions may apply] including those owned by your HQ";
      case "atc":
        return "Search for ATCs available in the XEVMPD [visibility restrictions may apply] including those owned by your HQ";
      case "hlgt":
        return "Search HLGT Terms for the selected MedDRA version available in the XEVMPD";
      case "hlt":
        return "Search HLT Terms for the selected MedDRA version available in the XEVMPD";
      case "llt":
        return "Search Low Level Terms for the selected MedDRA version available in the XEVMPD";
      case "mah":
        return "Search for MAHs available in the XEVMPD [visibility restrictions may apply] including those owned by your HQ";
      case "mfl":
        return "Search for master file locations available in the XEVMPD [visibility restrictions may apply] including those owned by your HQ";
      case "pharmform":
        return "Search for pharmaceutical forms available in the XEVMPD [visibility restrictions may apply] including those owned by your HQ";
      case "ppiattachment":
      case "psiattachment":
        return "Search for attachments owned in the XEVMPD by your HQ [write 4 characters at least]";
      case "previousevcode":
        return "Search for Previous EV Code";
      case "pt":
        return "Search Preferred Terms for the selected MedDRA version available in the XEVMPD";
      case "qppv":
        return "Search for QPPVs registered under your organisation profile";
      case "senderid":
        return "Search for EudraVigilance IDs of organisations registered with EudraVigilance";
      case "soc":
        return "Search SOC Terms for the selected MedDRA version available in the XEVMPD";
      case "source":
        return "Search for sources available in the XEVMPD [visibility restrictions may apply] including those owned by your HQ";
      case "sponsor":
        return "Search for Sponsors available in the XEVMPD [visibility restrictions may apply] including those owned by your HQ";
      case "substanceapproved":
        return "Search for approved substances available in the XEVMPD [visibility restrictions may apply] including those owned by your HQ";
      case "substance":
      case "substanceparentcode":
        return "Search for substances available in the XEVMPD [visibility restrictions may apply] including those owned by your HQ";
      default:
        return "Search...";
    }
  }

  getPlaceHolderToolTipTemplate(): string {
    //Customized tooltip placeholder
    return this.getPlaceHolderTemplate() + " (Search executed while typing characters)";
  }

  private sortBegins(data: any[], prefix: string) {
    return data.sort((a, b) => {
      const aUpper = a['name'].toUpperCase();
      const bUpper = b['name'].toUpperCase();
      const pUpper = prefix.toUpperCase();

      const aStarts = aUpper.startsWith(pUpper);
      const bStarts = bUpper.startsWith(pUpper);

      if (aStarts && !bStarts) return -1;
      if (!aStarts && bStarts) return 1;

      return aUpper.localeCompare(bUpper, undefined, { sensitivity: 'base' });
    });
  }

  getSelectedValueTemplate() {
    return this.selectedValue ? this.selectedValue : "";
  }

  selectAll() {
    this.items?.forEach(c => {
      if (this.pageOfItems?.includes(c)) {
        c.checked = true;
      }
    });
  }

  loadSelected() {
    let elements = this.items?.filter(element => element.checked == true);
    if (elements!.length > 0) {
      let elementsMap = elements.map(element => ({ selectedCode: element.code, selectedName: element.name }))
      this.selectedElement.emit(elementsMap);
    } else {
      this.injector!.get(Art57Section).showTooltipMessage({ severity: 'warn', detail: 'No item seleted', life: environment.errorElapsedTime });
    }
  }

  markChecked($event: any, item: any) {
    if (item.checked) {
      item.checked = !item.checked;
    } else {
      item.checked = $event.target.checked;
    }
  }

}